import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Camera, X, BadgeCheck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const buildFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${url}`;
};

/**
 * Avatar card with upload/remove + verified badge + customer code badge.
 * Owns: file picker + uploading state. Notifies parent via onUpdated when the
 * /auth/me/avatar response lands with the refreshed user doc.
 */
const CustomerAvatarCard = ({ user, authClient, onUpdated }) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');

  const onPickAvatar = () => fileInputRef.current?.click();

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast({ title: 'Image too large (max 5 MB)', variant: 'destructive' });
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) return toast({ title: 'Use PNG, JPG, WEBP or GIF', variant: 'destructive' });
    setUploadingAvatar(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await authClient.post('/auth/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatarUrl(r.data?.avatar_url || '');
      localStorage.setItem('tgmart_user', JSON.stringify(r.data));
      window.dispatchEvent(new Event('tgmart-session-change'));
      onUpdated && onUpdated(r.data);
      toast({ title: 'Avatar updated' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setUploadingAvatar(false); }
  };

  const onRemoveAvatar = async () => {
    try {
      const r = await authClient.delete('/auth/me/avatar');
      setAvatarUrl('');
      localStorage.setItem('tgmart_user', JSON.stringify(r.data));
      window.dispatchEvent(new Event('tgmart-session-change'));
      onUpdated && onUpdated(r.data);
      toast({ title: 'Avatar removed' });
    } catch (err) {
      toast({ title: 'Remove failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-6 flex items-center gap-5 flex-wrap" data-testid="customer-avatar-card">
      <div className="relative">
        {avatarUrl ? (
          <img src={buildFileUrl(avatarUrl)} alt={user?.name || 'avatar'} className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" data-testid="customer-avatar-img" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#ee5e23] to-[#2c54b2] text-white flex items-center justify-center text-2xl font-bold" data-testid="customer-avatar-fallback">
            {(user?.name || user?.mobile || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <button type="button" onClick={onPickAvatar} disabled={uploadingAvatar} className="absolute -bottom-1 -right-1 bg-white border border-gray-300 rounded-full p-1.5 hover:bg-gray-100 shadow disabled:opacity-50" title="Change avatar" data-testid="avatar-pick-btn">
          <Camera className="w-3.5 h-3.5 text-gray-700" />
        </button>
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onAvatarChange} className="hidden" data-testid="avatar-file-input" />
      </div>
      <div className="flex-1 min-w-[160px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-base font-semibold text-gray-900">{user?.name || 'Your profile'}</div>
          {user?.verified && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5"
              title="Mobile OTP verified. Your account is trusted across TelanganaMart."
              data-testid="customer-verified-badge"
            >
              <BadgeCheck className="w-3 h-3" /> Verified Account
            </span>
          )}
        </div>
        {user?.customer_code && (
          <span className="inline-block mt-0.5 text-[11px] font-mono uppercase tracking-wider bg-orange-50 text-[#ee5e23] border border-orange-200 rounded px-1.5 py-0.5" data-testid="customer-code-badge">
            ID: {user.customer_code}
          </span>
        )}
        <div className="text-xs text-gray-500">+91 {user?.mobile || ''}</div>
        <div className="text-xs text-gray-400 mt-1">Upload a square image (PNG, JPG, WEBP). Max 5 MB.</div>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onPickAvatar} disabled={uploadingAvatar} data-testid="avatar-upload-btn">
          <Camera className="w-4 h-4 mr-2" /> {uploadingAvatar ? 'Uploading…' : (avatarUrl ? 'Change' : 'Upload')}
        </Button>
        {avatarUrl && (
          <Button type="button" variant="outline" onClick={onRemoveAvatar} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="avatar-remove-btn">
            <X className="w-4 h-4 mr-2" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default CustomerAvatarCard;
